import {unref} from 'vue';
import type {MaybeRef} from 'vue';
import type {NuxtError} from 'nuxt/app';
import {generateFrontendErrorId} from '@/utils';

export type ErrorWithApplicationErrorID = {
	statusCode?: number,
	statusMessage?: string,
	cause?: {
		status: number,
		statusText: string,
		body: {
			error: string,
			messages?: string[],
			applicationErrorId: string,
		},
	}
};

function isErrorWithApplicationErrorID(e: unknown): e is ErrorWithApplicationErrorID {
	return (
		!!e
		&& typeof e === 'object'
		&& 'cause' in e
		&& typeof e.cause === 'object'
		&& !!e.cause
		&& 'status' in e.cause
		&& typeof e.cause.status === 'number'
		&& 'statusText' in e.cause
		&& typeof e.cause.statusText === 'string'
		&& 'body' in e.cause
		&& !!e.cause.body
		&& typeof e.cause.body === 'object'
		&& 'applicationErrorId' in e.cause.body
		&& typeof e.cause.body.applicationErrorId === 'string'
	);
}

export default function useShowError(error: MaybeRef<unknown>) {
	const normalizedError = unref(error);

	if (!normalizedError) {
		return;
	}

	if (isErrorWithApplicationErrorID(normalizedError)) {
		showError({
			statusCode: normalizedError.cause?.status ?? normalizedError.statusCode,
			statusMessage: normalizedError.cause?.statusText,
			data: {applicationErrorId: normalizedError.cause?.body.applicationErrorId},
			cause: normalizedError.cause,
		});
	} else {
		const baseError = normalizedError as Partial<NuxtError>;

		// Extract statusCode BEFORE calling showError (same logic as plugin)
		const cause = (baseError as any).cause;
		const message = (baseError as any).message;
		let statusCode = baseError.statusCode;
		let statusMessage = baseError.statusMessage;

		if (message && typeof message === 'object' && 'statusCode' in message) {
			// Check if error.message is an H3Error object (not a string)
			statusCode = message.statusCode;
			statusMessage = message.statusMessage || statusMessage;
		} else if (cause?.message && typeof cause.message === 'object' && 'statusCode' in cause.message) {
			// Check nested statusCode in cause.message (deeply nested H3Error)
			statusCode = cause.message.statusCode;
			statusMessage = cause.message.statusMessage || statusMessage;
		} else if (cause && typeof cause === 'object' && 'statusCode' in cause) {
			// Check nested statusCode in cause
			statusCode = cause.statusCode;
			statusMessage = cause.statusMessage || statusMessage;
		} else if (cause && typeof cause === 'object' && 'status' in cause && 'statusText' in cause) {
			// Check ApiError in cause (has .status, not .statusCode)
			statusCode = cause.status;
			statusMessage = cause.statusText;
		}

		const applicationErrorId = generateFrontendErrorId();
		const data = typeof baseError.data === 'object' && baseError.data !== null
			? {...baseError.data, applicationErrorId}
			: {applicationErrorId};

		// Pass clean object with extracted statusCode
		showError({
			statusCode,
			statusMessage,
			message: baseError.message,
			data,
		});
	}
}
